import React, { useCallback } from "react";
import DropzoneV2 from "@ui/DropzoneV2";
import ModalForm from "@ui/Modal/Form";
import t from "../../../../../../../i18n";
import Modal from "@ui/Modal";
import { usePost } from "../../../../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";

const MAX_IMAGE_SIZE = 20971520;

export function BlockFileUploadModal({
  projectId,
  opened,
  onClose,
  onUploadSucceeded,
}: {
  projectId: string;
  opened: boolean;
  onClose: () => void;
  onUploadSucceeded: ({ filename, path }: { filename: string; path: string }) => void;
}) {
  const [file, setFile] = React.useState<File | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const upload = usePost("/file");
  const { addToast } = useToasts();

  const verify = useCallback((acceptedFiles: File[], rejectedFiles: File[]) => {
    if (rejectedFiles.length > 1) {
      return setError(t("project.dashboard.transaction.import_image.error.to_many_files"));
    }

    const file = acceptedFiles[0];
    if (!file) {
      return setError(t("project.dashboard.transaction.import_image.error.no_file"));
    }
    if (file.size > MAX_IMAGE_SIZE) {
      return setError(t("project.dashboard.transaction.import_image.error.file_too_large"));
    }
    if (!["image/jpg", "image/png", "image/svg+xml", "image/jpeg"].includes(file.type)) {
      return setError(t("project.dashboard.transaction.import_image.error.invalid_file_type"));
    }

    setFile(file);
    setError(null);
  }, []);

  const close = useCallback(() => {
    setFile(null);
    setError(null);
    onClose();
  }, [onClose]);

  const submit = useCallback(async () => {
    if (file === null) {
      return false;
    }
    const body = new FormData();
    body.append("path", `${projectId}/dashboard`);
    body.append("file", file);
    // @ts-ignore
    await upload({ body })
      .then(({ body: { filename, path } }) => {
        onUploadSucceeded({ filename, path });
        return true;
      })
      .catch(() => {
        addToast("error", t("project.dashboard.transaction.import_image.error.upload_error"));
        return false;
      });
    return true;
  }, [addToast, file, onUploadSucceeded, projectId, upload]);

  return (
    <Modal
      isOpen={opened}
      onRequestClose={close}
      title={t("project.dashboard.transaction.import_image.title")}
      size="l"
    >
      <ModalForm
        onSubmit={submit}
        onRequestClose={close}
        okButtonName={t("project.dashboard.actions.import")}
      >
        <DropzoneV2
          onDrop={verify}
          accept={[".jpeg", ".jpg", ".png", ".svg"]}
          maxSize={MAX_IMAGE_SIZE}
          nameFile={file?.name ?? ""}
          errorText={error}
          validation={file && !error ? file.size > 0 : undefined}
        />
      </ModalForm>
    </Modal>
  );
}
